import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useDebounce } from '../../../../../../util/useDebounce.js';
import { mergeProps } from '@react-aria/utils';

var useFormControl = function useFormControl(_ref) {
  var rootProps = _ref.rootProps,
    inputProps = _ref.inputProps,
    clearButtonProps = _ref.clearButtonProps,
    validation = _ref.validation,
    textareaProps = _ref.textareaProps,
    setState = _ref.setState,
    setValidationResult = _ref.setValidationResult;
  var controlContainerRef = React.useRef(null);
  var defaultValue = (inputProps === null || inputProps === void 0 ? void 0 : inputProps.defaultValue) || (textareaProps === null || textareaProps === void 0 ? void 0 : textareaProps.defaultValue) || '';
  var _React$useState = React.useState(defaultValue),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    value = _React$useState2[0],
    setValue = _React$useState2[1];
  var _React$useState3 = React.useState(rootProps !== null && rootProps !== void 0 && rootProps.isDisabled || rootProps !== null && rootProps !== void 0 && rootProps.isReadOnly ? false : !!(clearButtonProps !== null && clearButtonProps !== void 0 && clearButtonProps.isVisible || defaultValue)),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    clearButtonVisibility = _React$useState4[0],
    setClearButtonVisibility = _React$useState4[1];

  // reset form field value and hide clear button
  var resetFormField = React.useCallback(function () {
    setValue('');
    setClearButtonVisibility(false);
  }, []);

  // focus first interactive element in the form field
  var focusFirstInteractiveElement = React.useCallback(function () {
    var _controlContainerRef$;
    (_controlContainerRef$ = controlContainerRef.current) === null || _controlContainerRef$ === void 0 || (_controlContainerRef$ = _controlContainerRef$.querySelector('input, textarea, button')) === null || _controlContainerRef$ === void 0 || _controlContainerRef$.focus();
  }, [controlContainerRef]);

  // handle clear button visibility
  var handleClearButtonVisibility = React.useCallback(function (inputValue, readOnly, disabled) {
    var _controlContainerRef$2;
    var isVisible = inputValue.length && !readOnly && !disabled && ((_controlContainerRef$2 = controlContainerRef.current) === null || _controlContainerRef$2 === void 0 ? void 0 : _controlContainerRef$2.contains(document.activeElement));
    setClearButtonVisibility(isVisible);
  }, [controlContainerRef]);

  // validate input value
  var validateInput = React.useCallback(function (checkValidity, inputValue) {
    var _checkValidity = checkValidity(inputValue),
      isValid = _checkValidity.isValid,
      message = _checkValidity.message;
    setState(isValid ? 'success' : 'error');
    setValidationResult({
      message: message,
      isValid: isValid
    });
  }, [setState, setValidationResult]);
  var validateInputDebounced = useDebounce(validateInput, (validation === null || validation === void 0 ? void 0 : validation.debounceTime) || 0);
  var handleValidation = React.useCallback(function (inputValue) {
    if (validation) {
      if (validation.debounceTime) {
        validateInputDebounced(validation.checkValidity, inputValue);
      } else {
        validateInput(validation.checkValidity, inputValue);
      }
    }
  }, [validateInput, validateInputDebounced, validation]);

  // default clear button props
  var clearButtonDefaultProps = React.useMemo(function () {
    return {
      onPress: function onPress() {
        resetFormField();
        // handling validation
        handleValidation('');
        focusFirstInteractiveElement();
      },
      isVisible: clearButtonVisibility
    };
  }, [clearButtonVisibility, focusFirstInteractiveElement, handleValidation, resetFormField]);
  var handleChange = React.useCallback(function (e) {
    var _e$target = e.target,
      inputValue = _e$target.value,
      readOnly = _e$target.readOnly,
      disabled = _e$target.disabled;
    setValue(inputValue);

    // handling clear button visibility
    handleClearButtonVisibility(inputValue, readOnly, disabled);

    // handling validation
    handleValidation(inputValue);
  }, [handleClearButtonVisibility, handleValidation]);
  return {
    textareaProps: mergeProps({
      value: value,
      onChange: handleChange,
      containerRef: controlContainerRef
    }, textareaProps),
    inputProps: mergeProps({
      value: value,
      onChange: handleChange
    }, inputProps),
    clearButtonProps: mergeProps(clearButtonDefaultProps, clearButtonProps),
    controlContainerProps: {
      ref: controlContainerRef
    },
    utilityProps: {
      setValue: setValue,
      setClearButtonVisibility: setClearButtonVisibility,
      resetFormField: resetFormField,
      setState: setState,
      setValidationResult: setValidationResult
    }
  };
};

export { useFormControl };
//# sourceMappingURL=useFormControl.js.map
